if (!customElements.get('product-quantity-total')) {
  // Reads a price shown on the page ("690,00 zł", "1 450,00 zł") and returns it in cents.
  // Returns null if the text does not contain exactly one number (e.g. a price range).
  const parseMoneyToCents = (text) => {
    const groups = String(text || '').match(/\d[\d\s\u00a0\u202f.,']*/g);
    if (!groups || groups.length !== 1) return null;

    const raw = groups[0]
      .replace(/[\s\u00a0\u202f'.,]+$/, '')
      .replace(/[\s\u00a0\u202f']/g, '');
    const match = raw.match(/^(.*?)[.,](\d{1,2})$/);
    const whole = (match ? match[1] : raw).replace(/[.,]/g, '');
    const fraction = match ? match[2].padEnd(2, '0') : '00';
    const cents = Number(whole) * 100 + Number(fraction);

    return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
  };

  customElements.define('product-quantity-total', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();

      this.onQuantityChange = (event) => {
        const input = event.target;
        const form = document.getElementById(this.dataset.formId);

        // Shopify's hidden name="id" input shadows the form.id property.
        if (input.name !== 'quantity' || input.form !== form) return;

        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.updateTotal());
      };

      document.addEventListener('input', this.onQuantityChange, {
        signal: this.controller.signal
      });

      document.addEventListener('change', this.onQuantityChange, {
        signal: this.controller.signal
      });

      this.unsubscribe = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.quantityUpdate,
        () => this.updateTotal()
      );

      this.watchDisplayedPrice();
      this.updateTotal();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.unsubscribe?.();
      this.priceObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.priceFrame);
    }

    // The block is rendered as <div id="QuantityTotal-SECTION"><product-quantity-total>,
    // and the product price lives in <div id="price-SECTION">.
    get priceContainer() {
      const sectionId = this.parentElement?.id?.replace('QuantityTotal-', '');
      return sectionId ? document.getElementById(`price-${sectionId}`) : null;
    }

    // The YMQ Product Options app rewrites the displayed price when a size is chosen.
    // Watch that element so the total follows it.
    watchDisplayedPrice() {
      const container = this.priceContainer;
      if (!container || !('MutationObserver' in window)) return;

      this.priceObserver = new MutationObserver(() => {
        cancelAnimationFrame(this.priceFrame);
        this.priceFrame = requestAnimationFrame(() => this.updateTotal());
      });

      this.priceObserver.observe(container, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    readDisplayedPrice() {
      const container = this.priceContainer;
      if (!container) return null;

      const element =
        container.querySelector('.f-price--on-sale .f-price__sale .f-price-item--sale') ||
        container.querySelector('.f-price__regular .f-price-item--regular');

      return element ? parseMoneyToCents(element.textContent) : null;
    }

    updateTotal() {
      const state = JSON.parse(
        this.querySelector('[data-quantity-total-state]').textContent
      );

      const form = document.getElementById(this.dataset.formId);
      const input = form?.elements.namedItem('quantity');
      const quantity = input ? Number(input.value) : state.minimum;

      if (
        !Number.isSafeInteger(quantity) ||
        quantity < 1 ||
        (input && !input.validity.valid)
      ) return;

      const pricingQuantity = quantity + state.cartQuantity;
      let unitPrice = state.price;
      let appliedMinimum = 0;

      for (const priceBreak of state.breaks) {
        if (
          pricingQuantity >= priceBreak.minimum &&
          priceBreak.minimum > appliedMinimum
        ) {
          unitPrice = priceBreak.price;
          appliedMinimum = priceBreak.minimum;
        }
      }

      // If the price on the page differs from the Shopify variant price
      // (a size surcharge from YMQ), use the price on the page.
      const displayedPrice = this.readDisplayedPrice();
      if (displayedPrice !== null && displayedPrice !== state.price) {
        unitPrice = displayedPrice;
      }

      const total = unitPrice * quantity;
      if (!Number.isSafeInteger(total)) return;

      const target = this.querySelector('[data-quantity-total-value]');
      const formatted = FoxTheme.Currency.formatMoney(total, state.moneyFormat);
      if (target.innerHTML !== formatted) target.innerHTML = formatted;
    }
  });
}
